import { AlertCircle, User } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import "./App.css";
import * as api from "./api";
import EEGAnalysis from "./components/EEGAnalysis";
import PatientDashboard from "./components/PatientDashboard";
import SessionManager from "./components/SessionManager";
import BaseTemplate from "./templates/BaseTemplate";

function App() {
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPatientDetails, setSelectedPatientDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPatientSelector, setShowPatientSelector] = useState(false);
  const [isLeftSidebarHidden, setIsLeftSidebarHidden] = useState(false);

  const fetchPatients = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getPatients();
      setPatients(response.data || []);
    } catch (err) {
      console.error("Error fetching patients:", err);
      setError(err.response?.data?.detail || "Failed to fetch patients");
      setPatients([]);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const fetchPatientDetails = useCallback(async (patientId) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.getPatientById(patientId);
      setSelectedPatientDetails(response.data);
    } catch (err) {
      console.error("Error fetching patient details:", patientId, err);
      setError(
        err.response?.data?.detail ||
          `Failed to fetch patient details for ID: ${patientId}`
      );
      setSelectedPatientDetails(null);
    }
    setIsLoading(false);
  }, []);

  const removePatient = useCallback(async (patientId) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.deletePatient(patientId);
    } catch (err) {
      console.error("Error removing patient:", patientId, err);
      setError(
        err.response?.data?.detail ||
          `Failed to remove patient with ID: ${patientId}`
      );
    }
    setSelectedPatientDetails(null);
    setIsLoading(false);
  }, []);

  const removeSession = useCallback(async (sessionId) => {
    setIsLoading(true);
    setError(null);
    try {
      await api.deleteSession(sessionId);
    } catch (err) {
      console.error("Error removing session:", sessionId, err);
      setError(
        err.response?.data?.detail ||
          `Failed to remove session with ID: ${sessionId}`
      );
    }
    setIsLoading(false);
  }, []);

  const handleSelectPatient = useCallback(
    (patient) => {
      setSelectedPatient(patient);
      setSelectedPatientDetails(null);
      setSelectedSession(null);
      fetchPatientDetails(patient.id);

      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      }
    },
    [fetchPatientDetails]
  );

  const handleDeletePatient = useCallback(
    async (patientId) => {
      await removePatient(patientId);
      await fetchPatients();
    },
    [removePatient, fetchPatients]
  );

  const handleSelectSession = useCallback(
    (session) => {
      if (!session) {
        setSelectedSession(null);
        return;
      }

      const patientName =
        selectedPatientDetails?.name || selectedPatient?.name || "Patient";
      setSelectedSession({ ...session, patient_name: patientName });

      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      }
    },
    [selectedPatientDetails, selectedPatient]
  );

  const handleDeleteSession = useCallback(
    async (sessionId, patientId) => {
      await removeSession(sessionId);
      await fetchPatientDetails(patientId);
    },
    [removeSession, fetchPatientDetails]
  );

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  const handleShowPatientSelector = useCallback((show) => {
    setShowPatientSelector(show);
  }, []);

  return (
    <BaseTemplate className={"relative flex-row gap-4 h-full"}>
      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 flex justify-center items-start pt-8 z-10 pointer-events-none">
          <div className="flex items-center space-x-2 bg-white/80 px-4 py-2 rounded-lg shadow">
            <div className="w-5 h-5 rounded-full bg-blue-600 animate-pulse"></div>
            <p className="text-lg text-gray-500">Loading...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 p-4 bg-red-50 border border-red-200 rounded-lg shadow-lg">
          <p className="text-red-600 flex items-center">
            <AlertCircle className="h-5 w-5 mr-2" />
            Error: {error}
          </p>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 z-20 md:hidden"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Left Sidebar - Patients and Sessions */}
      <aside
        className={`flex flex-col gap-4 flex-shrink-0 h-full transition-all duration-300 ease-in-out ${
          isLeftSidebarHidden 
            ? "w-0 min-w-0 opacity-0 overflow-hidden" 
            : "w-[380px] min-w-[380px] opacity-100"
        } ${
          isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Patient Dashboard */}
        <div className="bg-white rounded-2xl shadow-2xl shadow-blue-900/5 overflow-hidden border border-gray-100 flex flex-col flex-shrink-0">
          <div className="p-4 bg-[#0B1120] text-white flex-shrink-0">
            <h2 className="text-lg font-semibold tracking-tight">Patients</h2>
          </div>
          <div className="p-3 md:p-4">
            <PatientDashboard
              patients={patients}
              fetchPatients={fetchPatients}
              onSelectPatient={handleSelectPatient}
              onDeletePatient={handleDeletePatient}
              selectedPatient={selectedPatient}
              showPatientSelector={showPatientSelector}
              onShowPatientSelector={handleShowPatientSelector}
            />
          </div>
        </div>

        {/* Session Manager */}
        {selectedPatient && !showPatientSelector && (
          <div className="bg-white rounded-2xl shadow-2xl shadow-blue-900/5 overflow-hidden flex-1 border border-gray-100 flex flex-col min-h-0">
            <div className="p-4 bg-[#0B1120] text-white flex-shrink-0">
              <h2 className="text-lg font-semibold tracking-tight">Sessions</h2>
            </div>
            <div className="p-3 md:p-4 overflow-auto flex-1">
              <SessionManager
                selectedPatient={selectedPatientDetails}
                fetchPatientDetails={fetchPatientDetails}
                onSelectSession={handleSelectSession}
                onDeleteSession={handleDeleteSession}
                selectedSessionForAnalysis={selectedSession}
              />
            </div>
          </div>
        )}
      </aside>

      {/* Main Content - EEG Analysis (Right Side) */}
      <section className="flex-1 min-w-0 h-full">
        {selectedPatient ? (
          <div className="bg-white rounded-2xl shadow-2xl shadow-blue-900/5 overflow-hidden h-full border border-gray-100 flex flex-col">
            <div className="p-4 bg-[#0B1120] text-white flex items-center justify-between flex-shrink-0">
              <h2 className="text-lg font-semibold tracking-tight">qEEG Analysis</h2>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <EEGAnalysis
                selectedSessionForAnalysis={selectedSession}
                key={selectedSession && selectedSession.id}
                isLeftSidebarHidden={isLeftSidebarHidden}
                setIsLeftSidebarHidden={setIsLeftSidebarHidden}
              />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-2xl shadow-blue-900/5 overflow-hidden border border-gray-100 h-full flex items-center justify-center">
            <div className="p-8 md:p-16 text-center max-w-md">
              <div className="w-24 h-24 mx-auto mb-8 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl flex items-center justify-center">
                <User
                  className="h-12 w-12 text-blue-500"
                  strokeWidth={1.5}
                />
              </div>
              <h3 className="text-2xl md:text-3xl font-semibold text-[#0B1120] tracking-tight">
                No Patient Selected
              </h3>
              <p className="mt-4 text-gray-500 text-base md:text-lg leading-relaxed">
                Please select a patient from the list to view sessions and perform qEEG analysis.
              </p>
            </div>
          </div>
        )}
      </section>
    </BaseTemplate>
  );
}

export default App;
